"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "@/components/ds/icons"

import { cn } from "@/lib/utils"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        // Overlay del DS: rgba(9,9,11,.35). Nunca negro puro.
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-[rgba(9,9,11,0.35)]",
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  onPointerDownOutside,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
  showCloseButton?: boolean
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        // Mismo criterio que DialogContent: el clic fuera no cierra el drawer,
        // porque casi siempre es el cierre de un popover/select interno y se
        // llevaba puesto el formulario en curso. ESC sí cierra. Pasar un
        // handler propio restaura el default de Radix.
        onPointerDownOutside={onPointerDownOutside ?? ((e) => e.preventDefault())}
        onInteractOutside={onInteractOutside ?? ((e) => e.preventDefault())}
        className={cn(
          // Drawer del DS: 560px normal (1060px cuando lleva dos columnas,
          // vía className), max-width 94vw y sombra lateral. Sin padding
          // propio — lo ponen Header/Body/Footer, para que sus divisores y el
          // fondo del pie lleguen de borde a borde. `overflow-hidden` + el
          // `overflow-y-auto` de SheetBody hacen que scrollee SOLO el cuerpo.
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex max-w-[94vw] flex-col overflow-hidden shadow-[var(--shadow-drawer)] transition ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-[220ms]",
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-full border-l sm:w-[560px]",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-full border-r sm:w-[560px]",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          // 30x30 radio 7, alineado con el padding del encabezado — el mismo
          // botón que la modal. Va absoluto para no obligar a cada llamador a
          // componerlo; SheetHeader reserva el espacio con su `pr`.
          <SheetPrimitive.Close className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:ring-ring absolute top-[13px] right-[14px] flex size-[30px] items-center justify-center rounded-[7px] transition-colors focus:ring-2 focus:outline-hidden disabled:pointer-events-none">
            <XIcon className="size-[19px]" />
            <span className="sr-only">Cerrar</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

/**
 * Encabezado: título + subtítulo, con divisor de borde a borde. El `pr` deja
 * libre la esquina donde `SheetContent` posiciona el botón de cerrar. Mismo
 * padding que `DialogHeader` — la modal y el drawer son el mismo producto.
 */
function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "border-border-subtle flex shrink-0 flex-col gap-0.5 border-b px-[18px] py-4 pr-[52px] text-left",
        className
      )}
      {...props}
    />
  )
}

/** Cuerpo del drawer — la ÚNICA parte que scrollea. Gemelo de `DialogBody`. */
function SheetBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-body"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto p-[18px]",
        className
      )}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      // Pie del DS: hint a la izquierda, Cancelar + primario a la derecha.
      className={cn(
        "bg-surface-muted border-border-subtle mt-auto flex shrink-0 items-center justify-end gap-[9px] border-t px-[18px] py-[13px]",
        className
      )}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "text-foreground text-base font-semibold tracking-[-0.015em]",
        className
      )}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-meta text-[12.5px]", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
